import React, { useEffect, useState } from "react";
import { Undo2, Download, CreditCard, CheckCircle2, XCircle, Ban, Wallet, AlertTriangle, TrendingUp, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import StatCard from "@/components/admin/StatCard";
import { useI18n } from "@/i18n/I18nContext";
import api, { API } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";

const PROVIDER_LABELS = { mollie: "Mollie (web)", google_play: "Google Play (app Android)", simulated: "Simulé (test/dev)" };

export default function AdminPaymentsTab() {
  const { t } = useI18n();
  const [payments, setPayments] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [clientErrors, setClientErrors] = useState([]);

  const load = () => {
    api.get("/admin/payments").then(({ data }) => setPayments(data));
    api.get("/admin/payments/analytics").then(({ data }) => setAnalytics(data));
    api.get("/admin/payments/client-errors").then(({ data }) => setClientErrors(data));
  };
  useEffect(() => { load(); }, []);

  const refund = async (id) => {
    await api.post(`/admin/payments/${id}/refund`, {});
    load();
  };

  const exportCsv = () => {
    const token = localStorage.getItem("ams_token");
    window.open(`${API}/admin/export/csv?type=payments&auth=${token}`, "_blank");
  };

  const ov = analytics?.overview;

  return (
    <div className="space-y-8">
      {analytics?.by_provider?.length > 0 && (
        <div data-testid="admin-payments-by-provider">
          <p className="mb-3 text-sm font-medium text-muted-foreground">Répartition par canal (Web vs App Android)</p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {analytics.by_provider.map((p) => (
              <div key={p.provider} className="rounded-2xl border border-border bg-card p-5">
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <Smartphone className="h-4 w-4 text-primary" />
                  {PROVIDER_LABELS[p.provider] || p.provider}
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                  <div><p className="text-muted-foreground">Tentatives</p><p className="font-semibold">{p.total}</p></div>
                  <div><p className="text-muted-foreground">Payés</p><p className="font-semibold text-success">{p.paid}</p></div>
                  <div><p className="text-muted-foreground">Conversion</p><p className={`font-semibold ${p.conversion_rate < 15 ? "text-destructive" : ""}`}>{p.conversion_rate}%</p></div>
                  <div><p className="text-muted-foreground">Encaissé</p><p className="font-semibold">{p.amount_paid}€</p></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {ov && (
        <div data-testid={ADMIN.paymentsAnalytics}>
          <p className="mb-3 text-sm font-medium text-muted-foreground">Conversion Mollie (web)</p>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <StatCard icon={CreditCard} label="Paiements créés" value={ov.total} />
            <StatCard icon={CheckCircle2} label="Payés" value={ov.paid} accent />
            <StatCard icon={TrendingUp} label="Taux de conversion" value={`${ov.conversion_rate}%`} accent />
            <StatCard icon={XCircle} label="Expirés" value={ov.expired} />
            <StatCard icon={Ban} label="Échoués / annulés" value={ov.failed + ov.canceled} />
            <StatCard icon={Wallet} label="Montant encaissé" value={`${ov.total_paid_amount}€`} accent />
            <StatCard icon={AlertTriangle} label="Montant expiré" value={`${ov.total_expired_amount}€`} />
            <StatCard icon={Undo2} label="Ouverts / en attente" value={ov.open_or_pending} />
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Note : quelques anciennes transactions "expirées" datent d'avant le passage en clé Mollie live et ne représentent pas de vrais paiements manqués. "Inconnu" = méthode/pays pas encore capturé (paiement créé avant ce suivi, ou webhook pas encore reçu).
          </p>

          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <BreakdownTable title="Par méthode de paiement" rows={analytics.by_method} />
            <BreakdownTable title="Par pays (carte)" rows={analytics.by_country} />
          </div>
        </div>
      )}

      {clientErrors.length > 0 && (
        <div data-testid="admin-payments-client-errors">
          <p className="mb-3 text-sm font-medium text-muted-foreground">
            Erreurs de paiement signalées par l'appareil ({clientErrors.length})
          </p>
          <div className="overflow-x-auto rounded-2xl border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="p-4">Date</th>
                  <th className="p-4">Canal</th>
                  <th className="p-4">Étape</th>
                  <th className="p-4">Erreur</th>
                </tr>
              </thead>
              <tbody>
                {clientErrors.slice(0, 100).map((e, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="p-4 text-xs text-muted-foreground">{new Date(e.created_at).toLocaleString("fr-FR")}</td>
                    <td className="p-4">{e.provider}</td>
                    <td className="p-4 text-muted-foreground">{e.step || "—"}</td>
                    <td className="p-4 font-mono text-xs">{e.error_name}{e.error_message ? `: ${e.error_message}` : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div>
        <div className="mb-4 flex justify-end">
          <Button variant="outline" className="rounded-full" data-testid={ADMIN.exportCsvBtn("payments")} onClick={exportCsv}>
            <Download className="mr-2 h-4 w-4" />
            {t("admin.exportCsv")}
          </Button>
        </div>
        <div className="overflow-x-auto rounded-2xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="p-4">ID</th>
                <th className="p-4">Type</th>
                <th className="p-4">Montant</th>
                <th className="p-4">Méthode</th>
                <th className="p-4">Pays</th>
                <th className="p-4">Statut</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.payment_id} className="border-t border-border">
                  <td className="p-4 font-mono text-xs">{p.payment_id}</td>
                  <td className="p-4 capitalize text-muted-foreground">{p.purpose}</td>
                  <td className="p-4 font-semibold">{p.amount}€</td>
                  <td className="p-4 text-muted-foreground">{p.method || "—"}</td>
                  <td className="p-4 text-muted-foreground">{p.country || "—"}</td>
                  <td className="p-4"><Badge variant={p.status === "refunded" ? "secondary" : "outline"} className="rounded-full">{p.status}</Badge></td>
                  <td className="p-4">
                    {p.status === "paid" && (
                      <Button size="sm" variant="outline" className="rounded-full" data-testid={ADMIN.refundBtn(p.payment_id)} onClick={() => refund(p.payment_id)}>
                        <Undo2 className="mr-1 h-3.5 w-3.5" />
                        {t("admin.refund")}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function BreakdownTable({ title, rows }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <p className="mb-3 text-sm font-medium text-muted-foreground">{title}</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="py-2">Clé</th>
              <th className="py-2">Créés</th>
              <th className="py-2">Payés</th>
              <th className="py-2">Expirés</th>
              <th className="py-2">Conversion</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className="border-t border-border">
                <td className="py-2 font-medium">{r.key}</td>
                <td className="py-2">{r.created}</td>
                <td className="py-2 text-success">{r.paid}</td>
                <td className="py-2">{r.expired}</td>
                <td className="py-2 font-semibold">{r.conversion_rate}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
